import { Dispatch } from 'react';
import { IExclusionData, IWicExclusionAction, IWicExclusionsActions } from './types';
import { get, post, put, remove } from '../../communication/httpClient';
import {
  LOAD_CATEGORIES,
  ERROR_DATA_CATEGORIES,
  CATEGORIES_LOADED,
  SET_CURRENT_AGENCY,
  LOAD_EXCLUSIONS,
  EXCLUSIONS_LOADED,
  ERROR_DATA_EXCLUSIONS,
  RESET_EXCLUSIONS,
  RESET,
  ADDED_EXCLUSION,
  EXCLUSIONS_ACTION_ERROR,
  MULTIPLE_EXCLUSIONS_ADDED,
  EXCLUSION_EDITED,
} from './actionTypes';

export const getActions = (dispatch: Dispatch<IWicExclusionAction>): IWicExclusionsActions => ({
  loadCategories: async (stateIdentifierCode: string) => {
    dispatch({
      type: LOAD_CATEGORIES,
    });
    const { data, error } = await get(`/api/wic-exclusions/categories/${stateIdentifierCode}`);
    if (data) {
      dispatch({
        type: CATEGORIES_LOADED,
        payload: { stateIdentifierCode, data },
      });
    } else {
      dispatch({
        type: ERROR_DATA_CATEGORIES,
        payload: error,
      });
    }
  },
  setCurrentAgency: (agency) => {
    dispatch({
      type: SET_CURRENT_AGENCY,
      payload: agency,
    });
  },
  findExclusions: async (request) => {
    dispatch({
      type: LOAD_EXCLUSIONS,
    });
    const { data, error } = await post('/api/wic-exclusions', request);
    if (data) {
      dispatch({
        type: EXCLUSIONS_LOADED,
        payload: { data: data.data, lastPage: data.lastPage },
      });
    } else {
      dispatch({
        type: ERROR_DATA_EXCLUSIONS,
        payload: error,
      });
    }
  },
  addSingleExclusion: async (stateIdentifierCode, productCode, locations) => {
    if (locations.length === 0) {
      return;
    }
    const { data, error } = await put(`/api/wic-exclusions/${stateIdentifierCode}`, {
      excludedProducts: locations.map((location) => ({
        enterpriseUnit: location,
        upcOrPluCode: productCode,
      })),
    });
    if (data || !error) {
      dispatch({
        type: ADDED_EXCLUSION,
        payload: {
          productCode,
          locations,
        },
      });
    } else {
      dispatch({
        type: EXCLUSIONS_ACTION_ERROR,
        payload: error,
      });
    }
  },
  addMultipleExclusion: async (stateIdentifierCode, products, locations, exclusions) => {
    const productsCodes = {};
    const indexedLocations = {};
    for (const product of Object.keys(products)) {
      if (products[product].isSelected) {
        productsCodes[product] = true;
      }
    }
    for (const location of locations) {
      indexedLocations[location] = true;
    }
    const exclusionsToDelete: Array<IExclusionData> = [];
    const exclusionsToAdd: Array<IExclusionData> = [];
    for (const productCode of Object.keys(productsCodes)) {
      const product = exclusions.find((exclusion) => exclusion.upcOrPluCode === productCode);
      if (product) {
        for (const location of product.enterpriseUnit) {
          if (!indexedLocations[location]) {
            exclusionsToDelete.push({
              enterpriseUnit: location,
              upcOrPluCode: productCode,
            });
          }
        }

        for (const location of locations) {
          exclusionsToAdd.push({
            enterpriseUnit: location,
            upcOrPluCode: productCode,
          });
        }
      }
    }
    const requests: Array<Promise<any>> = [];
    if (exclusionsToDelete.length > 0) {
      requests.push(
        remove(`/api/wic-exclusions/${stateIdentifierCode}`, {
          data: {
            excludedProducts: exclusionsToDelete,
          },
        })
      );
    }
    if (exclusionsToAdd.length > 0) {
      requests.push(
        put(`/api/wic-exclusions/${stateIdentifierCode}`, {
          excludedProducts: exclusionsToAdd,
        })
      );
    }
    const results = await Promise.all(requests);
    for (const result of results) {
      if (result?.error) {
        dispatch({
          type: EXCLUSIONS_ACTION_ERROR,
          payload: result?.error,
        });
        return;
      }
    }
    dispatch({
      type: MULTIPLE_EXCLUSIONS_ADDED,
      payload: {
        products: productsCodes,
        locations,
      },
    });
  },
  editExclusion: async (stateIdentifierCode, productCode, locations, exclusions) => {
    const exclusion = exclusions.find((item) => item.upcOrPluCode === productCode);
    const exclusionsToDelete: Array<IExclusionData> = [];
    const exclusionsToAdd: Array<IExclusionData> = [];
    if (exclusion) {
      const indexedExclusionLocations = {};
      const indexedNewLocations = {};
      for (const location of exclusion.enterpriseUnit) {
        indexedExclusionLocations[location] = true;
      }
      for (const location of locations) {
        indexedNewLocations[location] = true;
        if (!indexedExclusionLocations[location]) {
          exclusionsToAdd.push({
            upcOrPluCode: productCode,
            enterpriseUnit: location,
          });
        }
      }
      for (const location of exclusion.enterpriseUnit) {
        if (!indexedNewLocations[location]) {
          exclusionsToDelete.push({
            upcOrPluCode: productCode,
            enterpriseUnit: location,
          });
        }
      }

      const requests: Array<Promise<any>> = [];
      if (exclusionsToAdd.length > 0) {
        requests.push(
          put(`/api/wic-exclusions/${stateIdentifierCode}`, {
            excludedProducts: exclusionsToAdd,
          })
        );
      }
      if (exclusionsToDelete.length > 0) {
        requests.push(
          remove(`/api/wic-exclusions/${stateIdentifierCode}`, {
            data: {
              excludedProducts: exclusionsToDelete,
            },
          })
        );
      }
      const results = await Promise.all(requests);
      for (const result of results) {
        if (result?.error) {
          dispatch({
            type: EXCLUSIONS_ACTION_ERROR,
            payload: result?.error,
          });
          return;
        }
      }
      dispatch({
        type: EXCLUSION_EDITED,
        payload: {
          item: productCode,
          locations: locations,
        },
      });
    }
  },
  resetExclusions: () => {
    dispatch({
      type: RESET_EXCLUSIONS,
    });
  },
  reset: () =>
    dispatch({
      type: RESET,
    }),
});
